import { StandardEvents } from '@shopify/events';

// This component enhances presentation and guards repeats. Shopify's existing
// product-form-component remains the sole owner of every cart request.
class OdProductPage extends HTMLElement {
  connectedCallback() {
    this.abort?.abort();
    this.resetQuantityControls();
    this.abort = new AbortController();
    const options = { signal: this.abort.signal };
    this.setupQuantityControls(options);
    this.pending ??= new Map();
    this.status = this.querySelector('[data-od-pdp-status]');
    this.variantForm = this.querySelector('[data-od-variant-form]');
    this.variantSelect = this.variantForm?.querySelector('[data-od-variant-select]');
    this.currentVariantId ??= this.variantSelect?.value;
    this.prepareNavigation();
    for (const [form, record] of this.pending) {
      if (!record.timer) this.startTimer(form, record);
    }
    window.addEventListener('submit', event => this.guardSubmit(event), { ...options, capture: true });
    this.addEventListener(StandardEvents.cartLinesUpdate, event => this.observeCart(event), options);
    this.addEventListener(StandardEvents.cartError, event => this.observeCartError(event), options);
    this.variantForm?.addEventListener('change', event => {
      if (!event.target.matches('[data-od-variant-select]')) return;
      if (this.pending.size) {
        this.variantSelect.value = this.currentVariantId;
        this.announce(this.dataset.odVariantBusy); return;
      }
      this.variantForm.requestSubmit();
    }, options);
    this.addEventListener('click', event => {
      if (!this.pending.size || !event.target.closest('[data-od-kit-link], .od-pdp__checkout')) return;
      event.preventDefault(); event.stopImmediatePropagation();
      this.announce(this.dataset.odVariantBusy);
    }, { ...options, capture: true });
  }

  setupQuantityControls(options) {
    this.quantity = this.querySelector('[data-od-product-quantity]');
    this.quantityControls = this.querySelector('[data-od-quantity-controls]');
    this.quantityButtons = [...this.quantityControls?.querySelectorAll('[data-od-quantity-step]') || []];
    if (!this.quantity || !this.quantityControls || this.quantityButtons.length !== 2 || typeof this.quantity.stepUp !== 'function') return;
    for (const button of this.quantityButtons) {
      button.addEventListener('click', () => {
        const next = this.quantityCandidate(Number(button.dataset.odQuantityStep));
        if (next === null) { this.updateQuantityButtons(); return; }
        this.quantity.value = next;
        // Native step operations do not emit input/change. Emit one of each for
        // existing observers, including the quantity-based payment example.
        this.quantity.dispatchEvent(new Event('input', { bubbles: true }));
        this.quantity.dispatchEvent(new Event('change', { bubbles: true }));
      }, options);
    }
    this.quantity.addEventListener('input', () => this.updateQuantityButtons(), options);
    this.quantity.addEventListener('change', () => this.updateQuantityButtons(), options);
    this.updateQuantityButtons();
    this.quantityControls.hidden = false;
    this.setAttribute('data-od-quantity-enhanced', '');
  }

  quantityCandidate(direction) {
    const input = this.quantity;
    if (!input || input.disabled || input.readOnly || ![1, -1].includes(direction)) return null;
    // Let the browser apply the original number input's min/step/max rules.
    // Probing off-DOM also disables a button before it can create an invalid
    // value when max is not aligned with the minimum and increment.
    const probe = input.cloneNode(false);
    probe.value = input.value;
    try { direction > 0 ? probe.stepUp() : probe.stepDown(); }
    catch { return null; }
    return probe.validity.valid && probe.value !== input.value ? probe.value : null;
  }

  updateQuantityButtons() {
    for (const button of this.quantityButtons || []) {
      button.disabled = this.quantityCandidate(Number(button.dataset.odQuantityStep)) === null;
    }
  }

  resetQuantityControls() {
    if (this.quantityControls) this.quantityControls.hidden = true;
    this.removeAttribute('data-od-quantity-enhanced');
  }

  prepareNavigation() {
    const current = new URL(window.location.href);
    const preview = current.searchParams.get('preview_theme_id');
    const requestedView = current.searchParams.get('view');
    const view = requestedView && /^[a-zA-Z0-9_-]{1,64}$/.test(requestedView) ? requestedView : null;
    if (this.variantForm) {
      if (view) {
        let input = this.variantForm.querySelector('input[name="view"]');
        if (!input) {
          input = document.createElement('input'); input.type = 'hidden';
          input.name = 'view'; this.variantForm.append(input);
        }
        input.value = view;
      }
      if (preview) {
        let input = this.variantForm.querySelector('input[name="preview_theme_id"]');
        if (!input) {
          input = document.createElement('input'); input.type = 'hidden';
          input.name = 'preview_theme_id'; this.variantForm.append(input);
        }
        input.value = preview;
      }
    }
    for (const link of this.querySelectorAll('[data-od-kit-link]')) {
      const url = new URL(link.href, current);
      if (url.origin !== current.origin) continue;
      if (preview) url.searchParams.set('preview_theme_id', preview);
      if (view) url.searchParams.set('view', view);
      link.href = url.href;
    }
  }

  guardSubmit(event) {
    const form = event.target instanceof HTMLFormElement ? event.target : null;
    if (!form || !this.contains(form)) return;
    if (form === this.variantForm) {
      if (this.pending.size) {
        event.preventDefault(); event.stopImmediatePropagation();
        this.announce(this.dataset.odVariantBusy);
      }
      return;
    }
    const wrapper = form.closest('[data-od-pdp-native-form]');
    if (!wrapper || !customElements.get('product-form-component')) return;
    if (this.pending.has(form)) { event.preventDefault(); event.stopImmediatePropagation(); return; }
    if (event.defaultPrevented || !form.checkValidity()) return;
    const button = form.querySelector('[data-od-pdp-add]');
    if (!button || button.disabled) return;
    const record = {
      wrapper, button, label: button.getAttribute('aria-label'), serverError: '',
      timer: null, startedAt: Date.now(), checkout: form.querySelector('.od-pdp__checkout'),
      variantSelect: this.variantSelect,
    };
    if (record.checkout) record.checkoutWasInert = record.checkout.inert;
    if (record.variantSelect) {
      record.variantSelectWasDisabled = record.variantSelect.disabled;
      record.variantSelect.disabled = true;
    }
    this.pending.set(form, record);
    wrapper.setAttribute('data-od-pending', '');
    form.setAttribute('aria-busy', 'true');
    // A real disabled attribute here would make the native submit handler refuse
    // the initial request. Repeats are stopped by this window-capture guard.
    button.setAttribute('aria-disabled', 'true');
    if (record.checkout) record.checkout.inert = true;
    button.removeAttribute('data-od-added');
    wrapper.querySelector('[ref="addToCartTextError"]')?.classList.add('hidden');
    this.announce(this.dataset.odLoading);
    this.startTimer(form, record);
  }

  startTimer(form, record) {
    record.timer = setTimeout(() => {
      if (this.pending.get(form) === record) this.settle(form, false, this.dataset.odError);
    }, Math.max(0, 30000 - (Date.now() - record.startedAt)));
  }

  observeCart(event) {
    const wrapper = event.target.closest?.('[data-od-pdp-native-form]');
    if (!wrapper || !this.contains(wrapper)) return;
    const form = wrapper.querySelector('form');
    const record = form && this.pending.get(form);
    if (!record || !event.promise) return;
    // A late response from a timed-out submission cannot settle a newer add.
    Promise.resolve(event.promise).then(
      result => {
        if (this.pending.get(form) === record) this.settle(form, !result?.detail?.didError, record.serverError || this.dataset.odError);
      },
      () => {
        if (this.pending.get(form) === record) this.settle(form, false, record.serverError || this.dataset.odError);
      }
    );
  }

  observeCartError(event) {
    const wrapper = event.target.closest?.('[data-od-pdp-native-form]');
    if (!wrapper || !this.contains(wrapper)) return;
    const record = this.pending.get(wrapper.querySelector('form'));
    if (record) record.serverError = typeof event.detail?.description === 'string' ? event.detail.description : this.dataset.odError;
  }

  settle(form, successful, message) {
    const record = this.pending.get(form);
    if (!record) return;
    clearTimeout(record.timer);
    this.pending.delete(form);
    record.wrapper.removeAttribute('data-od-pending');
    form.removeAttribute('aria-busy');
    record.button.removeAttribute('aria-disabled');
    if (record.label === null) record.button.removeAttribute('aria-label');
    else record.button.setAttribute('aria-label', record.label);
    if (record.checkout) record.checkout.inert = record.checkoutWasInert;
    if (record.variantSelect) record.variantSelect.disabled = record.variantSelectWasDisabled;
    const text = successful ? this.dataset.odSuccess : message;
    if (successful) record.button.setAttribute('data-od-added', '');
    else {
      const notice = record.wrapper.querySelector('[ref="addToCartTextError"]');
      if (notice) { notice.textContent = text; notice.classList.remove('hidden'); }
    }
    const live = record.wrapper.querySelector('[ref="liveRegion"]');
    if (live) live.textContent = text;
    this.announce(text);
  }

  announce(message) { if (this.isConnected && this.status) this.status.textContent = message || ''; }

  disconnectedCallback() {
    this.abort?.abort();
    this.resetQuantityControls();
    for (const record of this.pending?.values() || []) {
      clearTimeout(record.timer); record.timer = null;
    }
  }
}

class OdProductGallery extends HTMLElement {
  connectedCallback() {
    this.abort?.abort();
    this.abort = new AbortController();
    const options = { signal: this.abort.signal };
    this.media = [...this.querySelectorAll('[data-od-media]')];
    this.thumbs = [...this.querySelectorAll('[data-od-thumb]')];
    this.viewport = this.querySelector('[data-od-gallery-viewport]');
    this.status = this.querySelector('[data-od-gallery-status]');
    this.previous = this.querySelector('[data-od-gallery-prev]');
    this.next = this.querySelector('[data-od-gallery-next]');
    if (!this.media.length || !this.viewport) return;
    let hash = '';
    try { hash = decodeURIComponent(window.location.hash.slice(1)); } catch { /* Invalid fragment: use the real variant media. */ }
    const hashIndex = hash ? this.media.findIndex(medium => medium.id === hash) : -1;
    this.index = Math.max(0, Math.min(this.media.length - 1, hashIndex >= 0 ? hashIndex : this.index ?? (parseInt(this.dataset.odInitialIndex, 10) || 0)));
    this.addEventListener('click', event => {
      if (this.suppressClickUntil > Date.now() && this.viewport.contains(event.target)) {
        event.preventDefault(); event.stopImmediatePropagation(); return;
      }
      const thumb = event.target.closest('[data-od-thumb]');
      if (!thumb || event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
      event.preventDefault(); this.select(Number(thumb.dataset.odThumb));
    }, options);
    this.previous?.addEventListener('click', () => this.select(this.index - 1), options);
    this.next?.addEventListener('click', () => this.select(this.index + 1), options);
    this.addEventListener('keydown', event => {
      const thumb = event.target.closest('[data-od-thumb]');
      // Video/model keyboard controls and image links retain their native keys.
      if (!thumb && event.target !== this.viewport) return;
      const base = thumb ? Number(thumb.dataset.odThumb) : this.index;
      let next;
      if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = base - 1;
      else if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = base + 1;
      else if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = this.media.length - 1;
      else return;
      event.preventDefault(); this.select(next);
      if (thumb) this.thumbs[this.index]?.focus({ preventScroll: true });
    }, options);
    this.viewport.addEventListener('pointerdown', event => {
      if (event.pointerType !== 'touch' || event.target.closest('video, iframe, model-viewer')) return;
      this.touch = { id: event.pointerId, x: event.clientX, y: event.clientY };
    }, { ...options, passive: true });
    this.viewport.addEventListener('pointerup', event => {
      const start = this.touch; this.touch = null;
      if (!start || start.id !== event.pointerId) return;
      const dx = event.clientX - start.x; const dy = event.clientY - start.y;
      if (Math.abs(dx) > 48 && Math.abs(dx) > Math.abs(dy) * 1.5) {
        this.suppressClickUntil = Date.now() + 400;
        this.select(this.index + (dx < 0 ? 1 : -1));
      }
    }, { ...options, passive: true });
    this.viewport.addEventListener('pointercancel', () => { this.touch = null; }, options);
    this.classList.add('is-ready');
    const controls = this.querySelector('[data-od-gallery-controls]');
    if (controls) controls.hidden = this.media.length < 2;
    this.select(this.index, false);
  }

  select(index, scrollThumb = true) {
    if (!this.media?.length) return;
    this.index = Math.max(0, Math.min(this.media.length - 1, index));
    this.media.forEach((medium, position) => {
      const selected = position === this.index;
      if (!selected && !medium.hidden) this.pause(medium);
      medium.hidden = !selected;
    });
    this.thumbs.forEach((thumb, position) => {
      if (position === this.index) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
    });
    if (this.previous) this.previous.disabled = this.index === 0;
    if (this.next) this.next.disabled = this.index === this.media.length - 1;
    if (this.status) this.status.textContent = `${this.dataset.odMediaLabel || ''} ${this.index + 1} / ${this.media.length}`;
    if (scrollThumb) this.thumbs[this.index]?.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'auto' });
  }

  pause(medium) {
    medium.querySelectorAll('video, audio, model-viewer').forEach(player => player.pause?.());
    medium.querySelectorAll('product-model').forEach(player => player.pauseMedia?.());
    for (const iframe of medium.querySelectorAll('iframe')) {
      try {
        const origin = new URL(iframe.src).origin;
        if (/^https:\/\/(www\.)?(youtube\.com|youtube-nocookie\.com)$/.test(origin)) iframe.contentWindow?.postMessage(JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] }), origin);
        else if (origin === 'https://player.vimeo.com') iframe.contentWindow?.postMessage({ method: 'pause' }, origin);
      } catch { /* A missing/invalid external media URL has no player to pause. */ }
    }
  }

  disconnectedCallback() {
    this.abort?.abort(); this.touch = null; this.suppressClickUntil = 0;
    this.media?.forEach(medium => this.pause(medium));
  }
}

if (!customElements.get('od-product-page')) customElements.define('od-product-page', OdProductPage);
if (!customElements.get('od-product-gallery')) customElements.define('od-product-gallery', OdProductGallery);
